import React, { useEffect, useRef, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import {
  Button,
  ButtonBase,
  Checkbox,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';

import type { MemberSearch } from '~/hooks/apis/reporting/lensFilterMembers';

import { includedAfterToggle } from './filterGrouping';
import { memberLabel } from './format';

export const SEARCH_FROM = 9;
export type PickOption = { value: string; label: React.ReactNode; disabled?: boolean };

type PickPopoverProps = {
  label: string;
  options: Array<PickOption>;
  picked: Array<string>;
  single?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  onPick: (picked: Array<string>) => void;
  children: React.ReactNode;
  overlay?: boolean;
  triggerSx?: SxProps<Theme>;
  empty?: string;
  onOpen?: () => void;
  search?: MemberSearch;
  allMeansUnrestricted?: boolean;
  scopeNote?: string;
};

export default function PickPopover({
  label,
  options,
  picked,
  single,
  clearable = true,
  disabled,
  onPick,
  children,
  overlay,
  triggerSx,
  empty = 'No match',
  onOpen,
  search,
  allMeansUnrestricted = false,
  scopeNote,
}: PickPopoverProps) {
  const [anchor, setAnchor] = useState<HTMLElement>();
  const [query, setQuery] = useState('');
  const [searchText, setSearchText] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearchText(query.trim()), 400);
    return () => clearTimeout(timer);
  }, [query]);
  const searchIdentity = JSON.stringify(search?.key);
  const [settledSearch, setSettledSearch] = useState({ identity: searchIdentity, search });
  useEffect(() => {
    const timer = setTimeout(() => setSettledSearch({ identity: searchIdentity, search }), 300);
    return () => clearTimeout(timer);
  }, [searchIdentity]);
  const searchCurrent = searchIdentity === settledSearch.identity;
  const remote = useInfiniteQuery({
    queryKey: [...(settledSearch.search?.key ?? ['local-pick-options']), searchText],
    enabled: Boolean(anchor && search && searchCurrent),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) => settledSearch.search.load(searchText, pageParam, signal),
    getNextPageParam: (page) => page.after,
    retry: false,
    staleTime: 60_000,
  });
  const notLinked = remote.data?.pages.some((page) => page.status === 'not_linked');
  const list = useRef<HTMLUListElement>(null);
  const keys = (event: React.KeyboardEvent) => {
    const options = Array.from(
      list.current?.querySelectorAll<HTMLElement>('[role=option]:not([aria-disabled=true])') ?? [],
    );
    const index = options.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      options[Math.max(0, Math.min(options.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))]?.focus();
    }
  };
  const close = () => {
    setAnchor(undefined);
    setQuery('');
    setSearchText('');
  };
  const tabOut = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') {
      return;
    }
    event.preventDefault();
    const trigger = anchor;
    close();
    trigger?.focus();
  };
  const waiting = Boolean(search && (!searchCurrent || query.trim() !== searchText || remote.isPending));
  const shown = search
    ? waiting
      ? []
      : [...new Set([...(searchText ? [] : picked), ...(remote.data?.pages.flatMap((page) => page.values) ?? [])])].map(
          (value) => ({ value, label: memberLabel(value), disabled: false }),
        )
    : options.filter((option) => String(option.label).toLowerCase().includes(query.trim().toLowerCase()));
  const choose = (value: string) => {
    onPick(
      single
        ? clearable && picked.includes(value)
          ? []
          : [value]
        : allMeansUnrestricted
          ? includedAfterToggle(
              picked,
              options.map((option) => option.value),
              value,
            )
          : picked.includes(value)
            ? picked.filter((entry) => entry !== value)
            : [...picked, value],
    );
    if (single) {
      close();
    }
  };
  return (
    <>
      <ButtonBase
        aria-expanded={Boolean(anchor)}
        aria-haspopup="listbox"
        aria-label={label}
        disabled={disabled}
        onClick={(event) => {
          setAnchor(event.currentTarget);
          onOpen?.();
        }}
        sx={
          triggerSx ??
          (overlay
            ? { position: 'absolute', inset: 0, borderRadius: 'inherit' }
            : {
                bgcolor: 'action.hover',
                borderRadius: '8px',
                fontSize: 13,
                justifyContent: 'space-between',
                minHeight: 28,
                px: 1,
                textAlign: 'left',
                width: '100%',
              })
        }
      >
        {overlay ? null : children}
      </ButtonBase>
      <Popover
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={close}
        open={Boolean(anchor)}
        slotProps={{ paper: { onKeyDown: tabOut, sx: { minWidth: 220, maxWidth: 360 } } }}
      >
        {scopeNote ? (
          <Typography
            color="text.secondary"
            sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', fontSize: 12 }}
            role="status"
          >
            {scopeNote}
          </Typography>
        ) : null}
        {!single && clearable ? (
          <ListItemButton
            aria-label={`All ${label}`}
            onClick={() => onPick([])}
            selected={!picked.length}
            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 38 }}
          >
            <Checkbox checked={!picked.length} indeterminate={Boolean(picked.length)} size="small" tabIndex={-1} />
            <ListItemText primary="All" slotProps={{ primary: { fontSize: 13, fontWeight: 600 } }} />
            <Typography variant="caption" color="text.secondary">
              No restriction
            </Typography>
          </ListItemButton>
        ) : null}
        {search || options.length >= SEARCH_FROM ? (
          <InputBase
            autoFocus
            fullWidth
            inputProps={{ 'aria-label': `Find ${label}` }}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={keys}
            placeholder="Find"
            sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}
            value={query}
          />
        ) : null}
        <List
          aria-label={label}
          aria-multiselectable={!single}
          dense
          onKeyDown={keys}
          ref={list}
          role="listbox"
          sx={{ maxHeight: 300, overflow: 'auto' }}
        >
          {shown.map((option, index) => (
            <ListItemButton
              autoFocus={!search && options.length < SEARCH_FROM && index === 0}
              aria-selected={picked.includes(option.value) || (allMeansUnrestricted && !picked.length)}
              disabled={
                option.disabled || (allMeansUnrestricted && picked.length === 1 && picked.includes(option.value))
              }
              key={option.value}
              onClick={() => choose(option.value)}
              role="option"
              sx={{ minHeight: 28 }}
              selected={picked.includes(option.value) || (allMeansUnrestricted && !picked.length)}
            >
              {!single ? (
                <Checkbox
                  checked={picked.includes(option.value) || (allMeansUnrestricted && !picked.length)}
                  size="small"
                  tabIndex={-1}
                />
              ) : null}
              <ListItemText primary={option.label} slotProps={{ primary: { fontSize: 13 } }} />
              {!single ? (
                <Button
                  onClick={(event) => {
                    event.stopPropagation();
                    onPick([option.value]);
                  }}
                  size="small"
                >
                  Only
                </Button>
              ) : null}
            </ListItemButton>
          ))}
          {search && remote.isError ? (
            <Button onClick={() => void remote.refetch()} size="small">
              Couldn’t load choices. Retry
            </Button>
          ) : !shown.length ? (
            <Typography color="text.secondary" sx={{ p: 1.5 }}>
              {waiting ? 'Loading choices…' : notLinked ? 'Not linked. Scoped choices exceed the query budget.' : empty}
            </Typography>
          ) : null}
        </List>
        {search && notLinked && shown.length ? (
          <Typography variant="caption" sx={{ px: 1.5 }}>
            Choices could not be narrowed to your current filters.
          </Typography>
        ) : null}
        {search && remote.data?.pages.some((page) => page.complete === false) && !waiting ? (
          <Typography color="text.secondary" sx={{ px: 1.5, py: 0.5 }} variant="caption">
            This choice list may include values outside your current filters.
          </Typography>
        ) : null}
        {search && remote.hasNextPage && !waiting ? (
          <Button disabled={remote.isFetchingNextPage} onClick={() => void remote.fetchNextPage()} size="small">
            {remote.isFetchingNextPage ? 'Loading…' : 'Load more choices'}
          </Button>
        ) : null}
        {!single ? (
          <Typography
            color="text.secondary"
            sx={{ borderTop: 1, borderColor: 'divider', fontSize: 11.5, px: 1.5, py: 0.5 }}
          >
            Applies as you tick
          </Typography>
        ) : null}
        {clearable && (picked.length || (!single && query && shown.length)) ? (
          <Stack direction="row" sx={{ borderTop: 1, borderColor: 'divider', px: 1 }}>
            {!single && query && shown.length ? (
              <Button
                onClick={() =>
                  onPick([
                    ...new Set([
                      ...picked,
                      ...shown.filter((option) => !option.disabled).map((option) => option.value),
                    ]),
                  ])
                }
                size="small"
              >
                Select {shown.length} shown
              </Button>
            ) : null}
            {picked.length ? (
              <Button
                onClick={() => {
                  onPick([]);
                  if (single) {
                    close();
                  }
                }}
                size="small"
              >
                Clear
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Popover>
    </>
  );
}
